// src/pages/offlineSales/CreateOfflineSalesOrderPage.jsx
//
// Manual Offline Sales order creation page — counter/credit/courier sales with partial
// payment support. Structurally follows src/pages/orders/CreateOrderPage.jsx (customer
// section, item section, summary + fixed bottom action bar with .actionBarSpacer).
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Save, ShoppingCart, X, Store, User, Package, CreditCard,
  FileText, Truck, Store as StoreIcon, AlertTriangle,
  MapPin, UserCheck,
} from 'lucide-react';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import { usePermissions } from '../../hooks/usePermissions';
import useOfflineSalesForm from '../../hooks/useOfflineSalesForm';
import { getShops } from '../../services/api/shops';
import { getUserWarehouses } from '../../services/api/packingBatch';
import { getPaymentMethods } from '../../services/api/offlineSales/paymentMethods';
import { getCities } from '../../services/api/orderReference';
import { getActiveSession } from '../../services/api/offlineSales/cashierSessions';
import { uploadOrderPaymentProof, getSalesReps } from '../../services/api/offlineSales/orders';
import Dropdown from '../../components/ui/Dropdown/Dropdown';
import PaymentMethodPicker from '../../components/ui/PaymentMethodPicker/PaymentMethodPicker';
import PaymentRecordEntry from '../../components/common/PaymentRecordEntry/PaymentRecordEntry';
import CustomerSelectorModal from './components/CustomerSelectorModal';
import CityDeliverySection from '../orders/components/shared/CityDeliverySection';
import DeliveryChargeWeightSection from '../orders/components/shared/DeliveryChargeWeightSection';
import OfflineSalesItemsSection from './components/OfflineSalesItemsSection';
import { toSalesRepOptions } from './salesRepOptions';
import styles from './CreateOfflineSalesOrderPage.module.css';

const FULFILLMENT_METHODS = [
  { key: 'counter_pickup', icon: StoreIcon, labelKey: 'counterPickup' },
  { key: 'self_delivery', icon: Package, labelKey: 'selfDelivery' },
  { key: 'courier', icon: Truck, labelKey: 'courier' },
];


const CreateOfflineSalesOrderPage = () => {
  const navigate = useNavigate();
  const { t, formatCurrency } = useLocalization();
  const { success, error } = useAlert();
  const { hasPermission } = usePermissions();

  const {
    shopId, setShopId,
    customer, items, warehouseId, fulfillmentMethod, paymentDetails, orderDiscount, notes, salesRepUserId,
    cityId, weight, address, isCalculatingDelivery,
    errors, creditLimitWarning, submitting,
    setCustomer, setWarehouseId, setFulfillmentMethod, setOrderDiscount, setNotes, setSalesRepUserId,
    setCityId, setWeight, setAddress, setDeliveryChargeOverride,
    addItem, updateItem, removeItem,
    addPaymentDetail, updatePaymentDetail, removePaymentDetail,
    computeTotals,
    validateForm, submitOrder,
  } = useOfflineSalesForm();

  const [shops, setShops] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [cities, setCities] = useState([]);
  const [salesReps, setSalesReps] = useState([]);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeSession, setActiveSession] = useState(null);
  const [sessionLoading, setSessionLoading] = useState(false);

  const selectedShop = shops.find(s => s.id === shopId) || null;
  const totals = computeTotals(selectedShop);

  useEffect(() => {
    const loadReferenceData = async () => {
      try {
        const [shopsData, warehouseData, methodsData, citiesData, repsData] = await Promise.all([
          getShops({ assigned: true, type: 'pos' }),
          getUserWarehouses().catch(() => ({ warehouses: [] })),
          getPaymentMethods().catch(() => []),
          getCities().catch(() => ({ cities: [] })),
          getSalesReps().catch(() => ({ salesReps: [] })),
        ]);
        setSalesReps(repsData?.salesReps || []);
        const shopList = shopsData || [];
        setShops(shopList);
        if (shopList.length === 1) setShopId(shopList[0].id);
        const warehouseList = warehouseData?.warehouses || [];
        setWarehouses(warehouseList);
        if (warehouseList.length === 1) setWarehouseId(warehouseList[0].id);
        setPaymentMethods(Array.isArray(methodsData) ? methodsData : []);
        setCities(citiesData?.cities || []);
      } catch (err) {
        console.error('Failed to load reference data:', err);
      }
    };
    loadReferenceData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!shopId) {
      setActiveSession(null);
      return undefined;
    }

    let cancelled = false;
    setSessionLoading(true);
    getActiveSession(shopId)
      .then(result => {
        if (!cancelled) setActiveSession(result?.session || null);
      })
      .catch(() => {
        if (!cancelled) setActiveSession(null);
      })
      .finally(() => {
        if (!cancelled) setSessionLoading(false);
      });

    return () => { cancelled = true; };
  }, [shopId]);

  const handleAddProduct = (product) => {
    addItem(product, product.variationId || null, product.attributeDisplay || null);
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!validateForm()) {
      error(t('offlineSales.someFieldsRequired'));
      return;
    }
    if (sessionLoading) return;
    setIsSubmitting(true);
    try {
      const result = await submitOrder({ sessionId: activeSession?.id || null });
      if (result.success) {
        success(t('offlineSales.orderCreatedSuccess', { orderNumber: result.order.orderNumber }));
        if (!result.order.creditLimitExceeded) {
          navigate('/offline-sales/orders');
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.createOfflineSalesOrderPage}>
      <div className={styles.pageHeader}>
        <div className={styles.headerContent}>
          <div className={styles.headerTitle}>
            <ShoppingCart className={styles.headerIcon} size={22} />
            <div className={styles.titleContent}>
              <h1>{t('offlineSales.createOfflineSalesOrder')}</h1>
              <p>{t('offlineSales.addNewOfflineOrder')}</p>
            </div>
          </div>
          <button onClick={() => navigate(-1)} className={styles.backButton}>
            <ArrowLeft size={16} />
            {t('offlineSales.backToOrders')}
          </button>
        </div>
      </div>

      <div className={styles.pageContent}>
        {creditLimitWarning && (
          <div className={styles.creditLimitBanner}>
            <AlertTriangle size={18} />
            <div>
              <strong>{t('offlineSales.creditLimitWarningTitle')}</strong>
              <p>{t('offlineSales.creditLimitWarningMessage')}</p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className={styles.orderForm}>
          <div className={styles.topColumns}>
            <div className={styles.leftColumn}>
              {/* Shop selection */}
              <div className={styles.formSection}>
                <div className={styles.sectionHeader}>
                  <div className={styles.sectionHeaderLeft}>
                    <Store className={styles.sectionIcon} size={16} />
                    <h2>{t('offlineSales.shopSelection')}</h2>
                  </div>
                </div>
                <div className={styles.sectionBody}>
                  <Dropdown
                    value={shopId || ''}
                    onChange={value => setShopId(value ? parseInt(value) : null)}
                    options={shops.map(shop => ({ value: shop.id, label: shop.name }))}
                    placeholder={t('common.select')}
                    searchable
                    allowClear={false}
                    className={`${styles.fullWidthSelect} ${errors.shopId ? styles.errorDropdown : ''}`}
                  />
                  {errors.shopId && <span style={{ color: 'var(--color-danger)', fontSize: '0.75rem' }}>{errors.shopId}</span>}

                  {warehouses.length > 0 && (
                    <div style={{ marginTop: '0.625rem' }}>
                      <label className={styles.fieldLabel}>{t('offlineSales.warehouse')}</label>
                      <Dropdown
                        value={warehouseId || ''}
                        onChange={value => setWarehouseId(value ? parseInt(value) : null)}
                        options={warehouses.map(warehouse => ({ value: warehouse.id, label: warehouse.name }))}
                        placeholder={t('common.select')}
                        searchable
                        allowClear={false}
                        className={styles.fullWidthSelect}
                      />
                    </div>
                  )}
                  {selectedShop?.cashierSessionRequired && (
                    <div className={styles.sessionHint}>
                      {sessionLoading
                        ? t('offlineSales.sessionLoading')
                        : activeSession
                          ? `${t('offlineSales.sessionOpen')} #${activeSession.id}`
                          : t('offlineSales.sessionNotFound')}
                    </div>
                  )}
                </div>
              </div>

              {/* Customer */}
              <div className={styles.formSection}>
                <div className={styles.sectionHeader}>
                  <div className={styles.sectionHeaderLeft}>
                    <User className={styles.sectionIcon} size={16} />
                    <h2>{t('offlineSales.customerInformation')}</h2>
                  </div>
                </div>
                <div className={styles.sectionBody}>
                  <div className={`${styles.customerCard} ${errors.customer ? styles.customerCardError : ''}`}>
                    <div>
                      <div className={styles.customerCardName}>
                        {customer ? customer.name : t('offlineSales.cashSales')}
                      </div>
                      {customer && (
                        <div className={styles.customerCardMeta}>
                          {customer.phone || ''}
                          {customer.creditLimit != null && ` · ${t('offlineSales.creditLimit')}: ${formatCurrency(customer.creditLimit)}`}
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      className={styles.changeCustomerBtn}
                      onClick={() => setShowCustomerModal(true)}
                    >
                      {t('offlineSales.changeCustomer')}
                    </button>
                  </div>
                  {errors.customer && <span className={styles.errorText}>{errors.customer}</span>}
                </div>
              </div>

              {/* Sales representative */}
              <div className={styles.formSection}>
                <div className={styles.sectionHeader}>
                  <div className={styles.sectionHeaderLeft}>
                    <UserCheck className={styles.sectionIcon} size={16} />
                    <h2>{t('offlineSales.salesRep')}</h2>
                  </div>
                </div>
                <div className={styles.sectionBody}>
                  <Dropdown
                    value={salesRepUserId || ''}
                    onChange={value => setSalesRepUserId(value ? parseInt(value) : null)}
                    options={toSalesRepOptions(salesReps)}
                    placeholder={t('offlineSales.selectSalesRep')}
                    searchable
                    className={styles.fullWidthSelect}
                  />
                </div>
              </div>

              {/* Fulfillment method */}
              <div className={styles.formSection}>
                <div className={styles.sectionHeader}>
                  <div className={styles.sectionHeaderLeft}>
                    <Truck className={styles.sectionIcon} size={16} />
                    <h2>{t('offlineSales.fulfillmentMethod')}</h2>
                  </div>
                </div>
                <div className={styles.sectionBody}>
                  <div className={styles.fulfillmentOptions}>
                    {FULFILLMENT_METHODS.map(fm => {
                      const Icon = fm.icon;
                      return (
                        <button
                          type="button"
                          key={fm.key}
                          aria-pressed={fulfillmentMethod === fm.key}
                          className={`${styles.fulfillmentOption} ${fulfillmentMethod === fm.key ? styles.fulfillmentOptionActive : ''}`}
                          onClick={() => setFulfillmentMethod(fm.key)}
                        >
                          <Icon size={18} />
                          {t(`offlineSales.${fm.labelKey}`)}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              {fulfillmentMethod === 'courier' && (
                <div className={styles.formSection}>
                  <div className={styles.sectionHeader}>
                    <div className={styles.sectionHeaderLeft}>
                      <MapPin className={styles.sectionIcon} size={16} />
                      <h2>{t('offlineSales.deliveryDetails')}</h2>
                    </div>
                  </div>
                  <div className={styles.sectionBody}>
                    <div className={styles.formGroup} style={{ marginBottom: '0.75rem' }}>
                      <label>{t('offlineSales.deliveryAddress') || 'Delivery Address'} *</label>
                      <textarea
                        className={`${styles.notesTextarea} ${errors.address ? styles.errorInput : ''}`}
                        placeholder={t('offlineSales.deliveryAddressPlaceholder') || 'Enter the delivery address'}
                        value={address}
                        onChange={(e) => setAddress(e.target.value)}
                      />
                      {errors.address && <span className={styles.errorText}>{errors.address}</span>}
                    </div>
                    <CityDeliverySection
                      cityId={cityId}
                      cities={cities}
                      error={errors.cityId}
                      isCalculatingDelivery={isCalculatingDelivery}
                      onCityChange={setCityId}
                    />
                    <DeliveryChargeWeightSection
                      weight={weight}
                      onWeightChange={setWeight}
                      deliveryCharge={totals.deliveryCharge}
                      onDeliveryChargeChange={setDeliveryChargeOverride}
                      isCalculatingDelivery={isCalculatingDelivery}
                      canChangeDeliveryCharge={hasPermission('change_delivery_charge')}
                    />
                  </div>
                </div>
              )}

              {/* Notes */}
              <div className={styles.formSection}>
                <div className={styles.sectionHeader}>
                  <div className={styles.sectionHeaderLeft}>
                    <FileText className={styles.sectionIcon} size={16} />
                    <h2>{t('offlineSales.notes')}</h2>
                  </div>
                </div>
                <div className={styles.sectionBody}>
                  <textarea
                    className={styles.notesTextarea}
                    placeholder={t('offlineSales.notesPlaceholder')}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                  />
                </div>
              </div>
            </div>

            <div className={styles.rightColumn}>
              {/* Items */}
              <div className={styles.formSection}>
                <div className={styles.sectionHeader}>
                  <div className={styles.sectionHeaderLeft}>
                    <Package className={styles.sectionIcon} size={16} />
                    <h2>{t('offlineSales.orderItems')}</h2>
                  </div>
                </div>
                <div className={styles.sectionBody}>
                  <OfflineSalesItemsSection
                    shopId={shopId}
                    items={items}
                    errors={errors}
                    canChangeUnitPrice={hasPermission('change_offline_sales_unit_price')}
                    canGiveDiscount={hasPermission('give_discount')}
                    onAddItem={handleAddProduct}
                    onUpdateItem={updateItem}
                    onRemoveItem={removeItem}
                  />
                </div>
              </div>

              {/* Payment details */}
              <div className={styles.formSection}>
                <div className={styles.sectionHeader}>
                  <div className={styles.sectionHeaderLeft}>
                    <CreditCard className={styles.sectionIcon} size={16} />
                    <h2>{t('offlineSales.paymentDetails')}</h2>
                  </div>
                </div>
                <div className={styles.sectionBody}>
                  <PaymentMethodPicker
                    methods={paymentMethods}
                    onSelect={(key) => addPaymentDetail(key)}
                    disabled={totals.balanceAmount <= 0}
                    getTitle={(method) => totals.balanceAmount <= 0
                      ? (t('ecomOrders.noRemainingBalance') || 'No remaining balance to add a payment record')
                      : method.label}
                  />
                  {paymentDetails.map((p, index) => (
                    <PaymentRecordEntry
                      key={p.id}
                      index={index}
                      payment={p}
                      methods={paymentMethods}
                      title={t('offlineSales.paymentDetails')}
                      onChange={(field, value) => updatePaymentDetail(p.id, field, value)}
                      onRemove={() => removePaymentDetail(p.id)}
                      onUploadProof={uploadOrderPaymentProof}
                      maxAmount={totals.grandTotal}
                      errors={errors.paymentDetails?.[index] || {}}
                      enableCheque
                      idPrefix={`offline-${p.id}`}
                    />
                  ))}
                  {errors.creditSale && <span className={styles.errorText}>{errors.creditSale}</span>}
                </div>
              </div>

              {/* Summary */}
              <div className={styles.formSection}>
                <div className={styles.sectionHeader}>
                  <div className={styles.sectionHeaderLeft}>
                    <FileText className={styles.sectionIcon} size={16} />
                    <h2>{t('offlineSales.orderSummary')}</h2>
                  </div>
                </div>
                <div className={styles.sectionBody}>
                  <div className={styles.summaryRow}>
                    <span>{t('offlineSales.subtotal')}</span>
                    <span>{formatCurrency(totals.subtotal)}</span>
                  </div>
                  <div className={styles.summaryRow}>
                    <span>{t('offlineSales.orderDiscount')}</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      className={`${styles.discountInput} ${errors.orderDiscount ? styles.errorInput : ''}`}
                      value={orderDiscount}
                      onChange={(e) => setOrderDiscount(parseFloat(e.target.value) || 0)}
                    />
                  </div>
                  {errors.orderDiscount && <span className={styles.errorText}>{errors.orderDiscount}</span>}
                  <div className={styles.summaryRow}>
                    <span>{t('offlineSales.tax')}</span>
                    <span>{formatCurrency(totals.tax)}</span>
                  </div>
                  {fulfillmentMethod === 'courier' && totals.deliveryCharge > 0 && (
                    <div className={styles.summaryRow}>
                      <span>{t('offlineSales.deliveryCharge')}</span>
                      <span>+{formatCurrency(totals.deliveryCharge)}</span>
                    </div>
                  )}
                  <div className={`${styles.summaryRow} ${styles.summaryRowTotal}`}>
                    <span>{t('offlineSales.grandTotal')}</span>
                    <span>{formatCurrency(totals.grandTotal)}</span>
                  </div>
                  <div className={styles.summaryRow}>
                    <span>{t('offlineSales.paidAmount')}</span>
                    <span>{formatCurrency(totals.paidAmount)}</span>
                  </div>
                  {totals.balanceAmount > 0 && (
                    <div className={`${styles.summaryRow} ${styles.summaryRowBalance}`}>
                      <span>{t('offlineSales.balanceAmount')}</span>
                      <span>{formatCurrency(totals.balanceAmount)}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className={styles.actionBarSpacer} />
        </form>
      </div>

      <div className={styles.fixedActionBar}>
        <div className={styles.actionBarInner}>
          <div className={styles.actionBarMeta}>
            <span className={styles.actionBarTotal}>
              {t('offlineSales.grandTotal')}: <strong>{formatCurrency(totals.grandTotal)}</strong>
            </span>
            <span className={styles.actionBarTotal}>
              {items.length} {t('offlineSales.orderItems')}
            </span>
          </div>
          <div className={styles.actionBarButtons}>
            <button
              type="button"
              className={styles.cancelButton}
              onClick={() => navigate(-1)}
              disabled={isSubmitting || submitting}
            >
              <X size={15} />
              {t('common.cancel')}
            </button>
            <button
              type="button"
              className={styles.submitButton}
              onClick={handleSubmit}
              disabled={isSubmitting || submitting}
            >
              {isSubmitting || submitting ? (
                <div className={styles.loadingSpinner} />
              ) : (
                <>
                  <Save size={15} />
                  {t('offlineSales.createOrder')}
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {showCustomerModal && (
        <CustomerSelectorModal
          current={customer}
          onSelect={(c) => { setCustomer(c); if (c?.address) setAddress(c.address); setShowCustomerModal(false); }}
          onClose={() => setShowCustomerModal(false)}
        />
      )}
    </div>
  );
};

export default CreateOfflineSalesOrderPage;
